import {
  handleWithErrorMessage,
  handleWithGlobalMessage
} from "@/services/utils";
import api from "@/services/modules/applications";
import bookingsApi, {
  applicationIncludes
} from "@/services/modules/bookings/bookings";
import {
  SET_IS_FETCHING_AVAILABLE_CANDIDATES,
  SET_AVAILABLE_CANDIDATES,
  SET_IS_LOADING_APPLICATION_COMMENTS,
  SET_APPLICATION_COMMENTS,
  SET_IS_LOADING_APPLICATION_QUESTIONS,
  SET_BOOKING_APPLICATION_QUESTIONS,
  SET_IS_FETCHING_CURRENT_USER_APPLICATION,
  SET_IS_FETCHING_ALL_APPLICATIONS,
  SET_CURRENT_USER_APPLICATION,
  SET_ALL_APPLICATIONS,
  SET_IS_FETCHING_CURRENT_APPLICATION,
  SET_IS_FETCHING_CURRENT_APPLICATION_ANSWERS,
  SET_CURRENT_APPLICATION_ANSWERS,
  SET_CURRENT_APPLICATION
} from "@/store/modules/applications/mutations";
import { bookingApplicationUpdater } from "@/store/modules/applications/utils";
import {
  SET_CURRENT_BOOKING_CANDIDATES,
  UPDATE_LOCAL_APPLICATION
} from "@/store/modules/bookings/mutations";
import { BOOKINGS_NAMESPACE } from "@/store/modules/bookings/actions";
import { GET_CURRENT_BOOKING_CANDIDATES } from "@/store/modules/bookings/getters";
import { fromBookings } from "@/store/modules/bookings";
import { first } from "lodash";

export const ADD_ASSESSMENT = "ADD_ASSESSMENT";
export const WITHDRAW_CANDIDATE = "WITHDRAW_CANDIDATE";
export const APPROVE_CANDIDATE = "APPROVE_CANDIDATE";
export const SHORTLIST_CANDIDATE = "SHORTLIST_CANDIDATE";
export const REJECT_CANDIDATE = "REJECT_CANDIDATE";
export const CREATE_APPLICATION = "SUBMIT_CANDIDATES";
export const UNSORT_APPLICATION = "UNSORT_APPLICATION";
export const UPDATE_APPLICATION = "UPDATE_APPLICATION";
export const FETCH_AVAILABLE_CANDIDATES = "FETCH_AVAILABLE_CANDIDATES";
export const FETCH_ALL_APPLICATIONS = "FETCH_ALL_APPLICATIONS";
export const FETCH_CURRENT_APPLICATION = "FETCH_CURRENT_APPLICATION";
export const FETCH_CURRENT_APPLICATION_ANSWERS =
  "FETCH_CURRENT_APPLICATION_ANSWERS";
export const FETCH_APPLICATION_COMMENTS = "FETCH_APPLICATION_COMMENTS";
export const ADD_APPLICATION_COMMENT = "ADD_APPLICATION_COMMENT";
export const FETCH_BOOKING_APPLICATION_QUESTIONS =
  "FETCH_BOOKING_APPLICATION_QUESTIONS";
export const ADD_BOOKING_APPLICATION_QUESTION =
  "ADD_BOOKING_APPLICATION_QUESTION";
export const REMOVE_BOOKING_APPLICATION_QUESTION =
  "REMOVE_BOOKING_APPLICATION_QUESTION";
export const ADD_APPLICATION_ANSWER = "ADD_APPLICATION_ANSWER";
export const EDIT_APPLICATION_ANSWER = "EDIT_APPLICATION_ANSWER";
export const FETCH_USER_APPLICATION = "FETCH_USER_APPLICATION";
export const FETCH_APPLICATION_ON_BOOKING = "FETCH_APPLICATION_ON_BOOKING";

const actions = {
  async [ADD_ASSESSMENT](
    { dispatch, commit, rootGetters },
    { applicationId, assessment }
  ) {
    return bookingApplicationUpdater({
      dispatch,
      handler: async () => {
        const { data: createdAssessment } = await handleWithErrorMessage({
          request: () =>
            api(commit).addAssessment({ applicationId, assessment }),
          dispatch
        });
        const editedApplication = rootGetters[
          fromBookings(GET_CURRENT_BOOKING_CANDIDATES)
        ].find(({ id }) => id === applicationId);
        editedApplication.assessments = [
          ...editedApplication.assessments,
          createdAssessment
        ];
        commit(
          fromBookings(SET_CURRENT_BOOKING_CANDIDATES),
          editedApplication,
          { root: true }
        );
      }
    });
  },
  async [WITHDRAW_CANDIDATE]({ dispatch, commit }, applicationId) {
    return bookingApplicationUpdater({
      dispatch,
      handler: () =>
        handleWithErrorMessage({
          request: () => api(commit).withdrawCandidate(applicationId),
          dispatch
        })
    });
  },
  async [APPROVE_CANDIDATE]({ dispatch, commit }, applicationId) {
    return bookingApplicationUpdater({
      dispatch,
      handler: () =>
        handleWithErrorMessage({
          request: () => api(commit).approveCandidate(applicationId),
          dispatch
        })
    });
  },
  async [SHORTLIST_CANDIDATE]({ dispatch, commit }, applicationId) {
    return bookingApplicationUpdater({
      dispatch,
      handler: () =>
        handleWithErrorMessage({
          request: () => api(commit).shortlistCandidate(applicationId),
          dispatch
        })
    });
  },
  async [REJECT_CANDIDATE]({ dispatch, commit }, { applicationId, reason }) {
    return bookingApplicationUpdater({
      dispatch,
      handler: () =>
        handleWithErrorMessage({
          request: () => api(commit).rejectCandidate({ applicationId, reason }),
          dispatch
        })
    });
  },
  async [UNSORT_APPLICATION]({ dispatch, commit }, applicationId) {
    return bookingApplicationUpdater({
      dispatch,
      handler: () =>
        handleWithErrorMessage({
          request: () => api(commit).unsortApplication(applicationId),
          dispatch
        })
    });
  },
  async [CREATE_APPLICATION](
    { dispatch, commit },
    { booking, user, utm_source }
  ) {
    return handleWithErrorMessage({
      request: () =>
        api(commit).createApplication({ booking, user, utm_source }),
      dispatch,
      setDataFunction: ({ data }) => commit(SET_CURRENT_USER_APPLICATION, data)
    });
  },
  async [FETCH_AVAILABLE_CANDIDATES]({ dispatch, commit }, bookingId) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getBookingAvailableCandidates(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_AVAILABLE_CANDIDATES, isLoading),
      setDataFunction: ({ data }) => commit(SET_AVAILABLE_CANDIDATES, data)
    });
  },
  async [UPDATE_APPLICATION]({ dispatch, commit }, editedApplication) {
    const { data: fetchedApplication } = await handleWithGlobalMessage(
      () => api(commit).updateApplication(editedApplication),
      dispatch
    );
    commit(
      `${BOOKINGS_NAMESPACE}/${UPDATE_LOCAL_APPLICATION}`,
      fetchedApplication,
      { root: true }
    );
  },
  async [FETCH_APPLICATION_COMMENTS]({ dispatch, commit }, applicationId) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getApplicationComments(applicationId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_LOADING_APPLICATION_COMMENTS, isLoading),
      setDataFunction: ({ data }) => {
        commit(SET_APPLICATION_COMMENTS, data);
      }
    });
  },
  async [FETCH_BOOKING_APPLICATION_QUESTIONS]({ dispatch, commit }, bookingId) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getBookingApplicationQuestions(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_LOADING_APPLICATION_QUESTIONS, isLoading),
      setDataFunction: ({ data }) => {
        commit(SET_BOOKING_APPLICATION_QUESTIONS, data);
      }
    });
  },
  async [ADD_BOOKING_APPLICATION_QUESTION]({ dispatch, commit }, data) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).addBookingApplicationQuestion(data)
    });
  },
  async [REMOVE_BOOKING_APPLICATION_QUESTION]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).removeBookingApplicationQuestion(id)
    });
  },
  async [ADD_APPLICATION_COMMENT](
    { dispatch, commit },
    { applicationId, comment }
  ) {
    return await handleWithErrorMessage({
      dispatch,
      request: () => api(commit).addApplicationComment(applicationId, comment)
    });
  },
  async [ADD_APPLICATION_ANSWER]({ dispatch, commit }, data) {
    return handleWithErrorMessage({
      request: () => api(commit).createApplicationAnswer(data),
      dispatch
    });
  },
  async [EDIT_APPLICATION_ANSWER]({ dispatch, commit }, { data, answerId }) {
    return handleWithErrorMessage({
      request: () => api(commit).updateApplicationAnswer(data, answerId),
      dispatch
    });
  },
  async [FETCH_USER_APPLICATION]({ dispatch, commit }, { bookingId, userId }) {
    const params = {
      filter: {
        user_id: userId
      }
    };
    return handleWithErrorMessage({
      dispatch,
      request: () =>
        bookingsApi({ commit }).getBookingCandidates({
          bookingId,
          params
        }),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_USER_APPLICATION, isLoading),
      setDataFunction: ({ data }) =>
        commit(SET_CURRENT_USER_APPLICATION, first(data) || null)
    });
  },
  async [FETCH_ALL_APPLICATIONS]({ dispatch, commit }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getAllApplications(params),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_ALL_APPLICATIONS, isLoading),
      setDataFunction: ({ data }) => commit(SET_ALL_APPLICATIONS, data)
    });
  },
  async [FETCH_CURRENT_APPLICATION]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getCurrentApplication(id),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_APPLICATION, isLoading),
      setDataFunction: ({ data }) => commit(SET_CURRENT_APPLICATION, data)
    });
  },
  async [FETCH_CURRENT_APPLICATION_ANSWERS]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getCurrentApplicationAnswers(id),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_APPLICATION_ANSWERS, isLoading),
      setDataFunction: ({ data }) =>
        commit(SET_CURRENT_APPLICATION_ANSWERS, data)
    });
  },
  async [FETCH_APPLICATION_ON_BOOKING]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getCurrentApplication(id, applicationIncludes),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_APPLICATION, isLoading),
      setDataFunction: ({ data }) => commit(SET_CURRENT_APPLICATION, data)
    });
  }
};

export default actions;
